import React, { Component } from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import Cookies from "js-cookie"

import Input from "../input/Input"
import Textarea from "../input/Textarea"
import Select from "../input/Select"
import Button from "../button"

import FieldGroupName from "./fieldGroupName"
import FieldGroupCheckbox from "./fieldGroupCheckbox"

import countriesJson from "../../helpers/countries"

function getInputType(type) {
  switch (type.toLowerCase()) {
    case "phone":
      return "tel"
    default:
      return type
  }
}

function buildField(field, onChangeFunc, onFocusOutFunc) {
  switch (field.type.toLowerCase()) {
    case "text":
    case "email":
    case "phone":
      return (
        <Input
          key={field.id}
          id={field.id}
          label={field.label}
          type={getInputType(field.type)}
          required={field.required}
          hidden={field.hidden}
          hide_label={field.hide_label}
          value={field.value}
          onChange={onChangeFunc}
          onFocusOut={onFocusOutFunc}
          isValid={field.valid}
          disable={field.disable}
        />
      )
    case "textarea":
      return (
        <Textarea
          key={field.id}
          id={field.id}
          label={field.label}
          type={getInputType(field.type)}
          required={field.required}
          hidden={field.hidden}
          hide_label={field.hide_label}
          value={field.value}
          onChange={onChangeFunc}
          onFocusOut={onFocusOutFunc}
          isValid={field.valid}
          disable={field.disable}
        />
      )
    case "select":
      return (
        <Select
          key={field.id}
          id={field.id}
          label={field.label}
          required={field.required}
          hidden={field.hidden}
          hide_label={field.hide_label}
          defaultValue={field.default}
          onChange={onChangeFunc}
          onFocusOut={onFocusOutFunc}
          isValid={field.valid}
          disable={field.disable}
        >
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      )
    case "checkbox":
      return (
        <FieldGroupCheckbox
          key={field.id}
          onChange={onChangeFunc}
          onFocusOut={onFocusOutFunc}
          {...field}
        />
      )
    case "name":
      return (
        <FieldGroupName
          key={field.id}
          onChange={onChangeFunc}
          onFocusOut={onFocusOutFunc}
          {...field}
        />
      )
    case "richtext":
      return (
        <div
          className="form-control"
          key={field.id}
          dangerouslySetInnerHTML={{ __html: field.section_text }}
        />
      )
    default:
      console.info(`Handle ${field.type}`)
      return null
  }
}

function parseFormFields(apiFields, contact, prefillValues) {
  const parsedFields = {}
  apiFields.forEach((f) => {
    if (f.type === "name") {
      f.default = { first: contact.FirstName, last: contact.LastName }
    } else if (f.type === "email") {
      if (contact.Email !== "") f.default = contact.Email
    } else if (f.type === "phone") {
      if (contact.Phone !== null) f.default = contact.Phone
    } else if (f.type === "select" && f.name.startsWith("country")) {
      const countryCookie = Cookies.get("uCountry")
      if (contact.CountryCode) {
        f.default = contact.CountryCode
      } else if (contact.CountryOfResidence) {
        for (const countryCode in countriesJson) {
          if (
            Object.hasOwnProperty.call(countriesJson, countryCode) &&
            countriesJson[countryCode].toLowerCase() ===
              contact.CountryOfResidence.toLowerCase()
          ) {
            f.default = countryCode
            break
          }
        }
      } else if (countryCookie) f.default = countryCookie
    }

    for (const key of Object.keys(prefillValues)) {
      if (f.name.toLowerCase() === key.toLowerCase()) {
        f.default = prefillValues[key]
      }
    }

    const field = {
      id: f.id,
      label: f.label,
      placeholder: f.placeholder,
      hide_label: f.hide_label === "1",
      name: f.name,
      type: f.type,
      options: f.options,
      required: f.required === "1",
      hidden: f.hidden === "1",
      readonly: f.readonly === "1",
      logic: f.logic,
      default: f.default,
      section_text: f.section_text,
      sort: _.parseInt(f.sort),
      value: f.default,
      disable: false,
      valid: true,
    }
    parsedFields[f.id] = field
  })

  return parsedFields
}

function checkRequiredFieldValidation(f) {
  let valid = true
  if (f.required) {
    if (typeof f.value === "string" && f.value === "") {
      valid = false
    } else if (Array.isArray(f.value) && f.value.length === 0) {
      valid = false
    } else if (typeof f.value === "object") {
      const keys = _.keys(f.value)
      if (!keys.length) valid = false
      else
        keys.forEach((k) => {
          if (!f.value[k]) {
            valid = false
          }
        })
    }
  }

  return valid
}

class FormBuilder extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fields: parseFormFields(props.fields, props.contact, props.prefillValues),
      missingFields: [],
      isValid: false,
    }

    this.onValueChange = this.onValueChange.bind(this)
    this.onInputFocusOut = this.onInputFocusOut.bind(this)
    this.submitForm = this.submitForm.bind(this)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.loaded !== this.props.loaded)
      this.setState({
        fields: parseFormFields(
          this.props.fields,
          this.props.contact,
          this.props.prefillValues
        ),
      })

    if (
      prevProps.contact !== null &&
      prevProps.contact !== this.props.contact
    ) {
      this.setState({
        fields: parseFormFields(
          this.props.fields,
          this.props.contact,
          this.props.prefillValues
        ),
      })
    }
    if (
      prevProps.prefillValues !== null &&
      prevProps.prefillValues !== this.props.prefillValues
    ) {
      this.setState({
        fields: parseFormFields(
          this.props.fields,
          this.props.contact,
          this.props.prefillValues
        ),
      })
    }
  }

  requiredFieldsValid(manualFields) {
    const { fields } = this.state
    let valid = true
    const enabledFields = _.filter(manualFields || fields, (f) => {
      const field = this.checkLogic(f)
      return !field.disable
    })

    _.forEach(enabledFields, (f) => {
      if (!checkRequiredFieldValidation(f)) {
        valid = false
      }
    })

    return valid
  }

  onValueChange(id, value) {
    const fields = JSON.parse(JSON.stringify(this.state.fields))

    fields[id].value = value
    const htmlValidation = this.form.checkValidity()
    const reqValidation = this.requiredFieldsValid(fields)
    const isValid = htmlValidation && reqValidation

    this.setState({
      fields,
      isValid,
    })
  }

  onInputFocusOut(id, el) {
    const fields = JSON.parse(JSON.stringify(this.state.fields))
    const isValid = typeof el === "boolean" ? el : el.checkValidity()

    fields[id].valid = isValid && checkRequiredFieldValidation(fields[id])
    this.setState({ fields })
  }

  checkLogic(field) {
    const { logic } = field
    const { fields } = this.state

    if (logic) {
      const values = []
      let conditionsMet = false

      if (logic.checks) {
        logic.checks.forEach((check) => {
          const cValue = fields[check.field].value
          if (check.condition === "equals") {
            if (typeof cValue === "string") values.push(cValue === check.option)
            else values.push(_.indexOf(cValue, check.option) !== -1)
          } else if (check.condition === "notequals") {
            if (typeof cValue === "string") values.push(cValue !== check.option)
            else values.push(_.indexOf(cValue, check.option) === -1)
          }
        })
      }

      if (logic.conditional === "all") {
        conditionsMet = _.indexOf(values, false) === -1
      } else if (logic.conditional === "any") {
        conditionsMet = _.indexOf(values, true) !== -1
      }

      if (logic.action === "show") {
        field.hidden = !conditionsMet
        field.disable = !conditionsMet
      } else if (logic.action === "hide") {
        field.hidden = conditionsMet
        field.disable = conditionsMet
      }
    }

    return field
  }

  submitForm() {
    const isValid = this.form.checkValidity() && this.requiredFieldsValid()
    if (isValid) {
      const { fields } = this.state
      const { onSubmit } = this.props
      const data = {}
      let ftypeId = null
      let ftype = null

      _.forEach(
        _.filter(fields, (f) => {
          const field = this.checkLogic(f)
          return !field.disable
        }),
        (f) => {
          const fieldId = `field_${f.id}`
          data[fieldId] = f.value

          if (_.endsWith(f.name, "sf_formtype")) {
            ftypeId = fieldId
            ftype = ftype ? ftype : f.value
          }

          if (f.name === "printed_brochure") {
            if (f.value.length > 0) {
              ftype = "FBRO"
            } else {
              ftype = "EBRO"
            }
          }
        }
      )

      if (ftypeId && data[ftypeId]) {
        data[ftypeId] = ftype
      }

      onSubmit(data, fields)
    }
  }

  render() {
    const { fields, isValid } = this.state
    const { buttonText, submitting } = this.props
    return (
      <form ref={(el) => (this.form = el)}>
        {_.sortBy(fields, "sort").map((field) =>
          buildField(
            this.checkLogic(field),
            this.onValueChange,
            this.onInputFocusOut
          )
        )}
        <Button
          text={buttonText}
          className="btn--cta btn--sm-wide btn--submit"
          iconName="ArrowRight"
          onClick={this.submitForm}
          disabled={!(isValid && !submitting)}
        />
      </form>
    )
  }
}

FormBuilder.defaultProps = {
  prefillValues: {},
}

FormBuilder.propTypes = {
  loaded: PropTypes.bool.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      label: PropTypes.string,
      hide_label: PropTypes.string,
      default: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      section_text: PropTypes.string,
      options: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(
          PropTypes.shape({
            imageUrl: PropTypes.string,
            value: PropTypes.string,
            label: PropTypes.string,
          })
        ),
      ]),
      required: PropTypes.string,
      hidden: PropTypes.string,
      logic: PropTypes.shape({
        action: PropTypes.string,
        conditional: PropTypes.string,
        checks: PropTypes.arrayOf(
          PropTypes.shape({
            field: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            condition: PropTypes.string,
            option: PropTypes.string,
          })
        ),
      }),
    })
  ).isRequired,
  buttonText: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  contact: PropTypes.shape({
    FirstName: PropTypes.string,
  }),
  prefillValues: PropTypes.shape({}),
}

export default FormBuilder
